import { Message, Model } from '../../types/state'
import { RoomView } from '../constants/enums'
import { MfcUserNotepad } from '../libraries/mfc/MfcUserNotepad'
import { locales } from '../locales/en'
import {
    buildTipPayloadOptions,
    sendShareTip,
    sendTip,
    sendTipRepeatedly,
} from '../mfcLogic/api/tip'
import { getLens } from '../mfcLogic/lens'
import {
    FCOPT_BASICMUTE,
    FCOPT_GUESTMUTE,
    FCTYPE_CMESG,
    FCTYPE_PMESG,
    FCTYPE_ROOMDATA,
} from '../mfcLogic/mfcConstants'
import { toTokens } from '../mfcLogic/toTokens'
import {
    getTokenPurchaseInfo,
    shiftTokenStorage,
    tokensQualifyPurchase,
} from '../mfcLogic/tokens'
import { resolveChannel } from '../mfcLogic/userProperties'
import {
    UserAccess,
    isLevelPremiumOrHigher,
} from '../mfcLogic/userProperties/level'
import { isLounge } from '../mfcLogic/userProperties/user'
import { APPSTATE, HISTORY } from '../state'
import { getModalState, getRoomsState, getStateUser } from '../state/getState'
import { isQuickReplyOpen } from '../state/internal/floaters'
import { idToSlug, slugToID } from '../state/internal/users'
import { openChatCommandsDrawer } from '../state/modals/state.drawer'
import { setPMChatTextState } from '../state/state.pm'
import {
    openRoom,
    setLoungeSurrogate,
    setNextSurrogate,
    setPreviousSurrogate,
    setRoomMessagesState,
} from '../state/state.rooms'
import { AppRouter } from '../state/state.router'
import { routerPush } from '../utilities/router'
import { getNextOrPreviousModel } from './getNextOrPreviousModel'
import { inChatQuickBuyDialog } from './inChatQuickBuyDialog'
import { loadUserIfNeeded } from './loadUserIfNeeded'
import { openMessage } from './message'
import { goToHome } from './nav'
import { saveNote } from './notes'
import { openError } from './openError'
import {
    roomHelperSendBan,
    roomHelperSendCountdown,
    roomHelperSendKick,
    roomHelperSendMuteLevel,
    roomHelperSendMuteUser,
    roomHelperSendTopic,
} from './roomHelper'
import {
    isGuestUsername,
    isRoomHelper,
    resolveGuestSession,
} from './roomHelperHelpers'
import { sendPrivateMessage } from './sendPrivateMessage'
import { sendRoomMessage } from './sendRoomMessage'

type CommandPayload = {
    id?: number
    activeChat: Model
    args?: Array<string>
    chatType?: number
    isPremium?: boolean
    mainCommand?: string
    message?: string
}
type SyncCommand = (payload: CommandPayload) => void
type AsyncCommand = (payload: CommandPayload) => Promise<void>
type Command = SyncCommand | AsyncCommand
type Commands = Record<string, Command>

const COMMANDS: Commands = {
    '/?': helpCmd,
    '/help': helpCmd,
    '/awaymsg': noCmd,
    '/back': noCmd,
    '/h': homepageCmd,
    '/homepage': homepageCmd,
    '/l': lastCmd,
    '/last': lastCmd,
    '/n': nextCmd,
    '/next': nextCmd,
    '/p': prevCmd,
    '/prev': prevCmd,
    '/pm': pmCmd,
    '/w': whisperCmd,
    '/whisper': whisperCmd,
    '/me': meCmd,
    '/em': meCmd,
    '/sar': sarcasmCmd,
    '/sarcasm': sarcasmCmd,
    '/sarcastic': sarcasmCmd,
    '/note': noteCmd,
    '/note4': noteForCmd,
    '/notefor': noteForCmd,
    '/t': tipCmd,
    '/tip': tipCmd,
    '/tip4': tipForCmd,
    '/tipfor': tipForCmd,
    '/sharetip': shareTipCmd,
    '/qb': quickBuyCmd,
    '/quickbuy': quickBuyCmd,
    '/ts': toStorageCmd,
    '/tostorage': toStorageCmd,
    '/fs': fromStorageCmd,
    '/fromstorage': fromStorageCmd,

    // room helper commands
    '/topic': topicCmd,
    '/ban': banCmd,
    '/unban': unbanCmd,
    '/kick': kickCmd,
    '/mute': muteCmd,
    '/unmute': unmuteCmd,
    '/countdown': countdownCmd,
    '/c': countdownCmd,
}

export const generateChatCommands = async (
    message: string,
    user: Model,
    chatType: number,
) => {
    const { level } = getStateUser(slugToID['me'])
    const isPremium = isLevelPremiumOrHigher(level)
    const args = message.split(' ')
    const mainCommand = args[0]
    const cmd = COMMANDS[mainCommand]
    const payload: CommandPayload = {
        activeChat: user,
        args,
        chatType,
        isPremium,
        mainCommand,
        message,
    }
    if (!cmd) {
        return defaultCmd(payload)
    }
    return cmd(payload)
}

function helpCmd({ activeChat }: CommandPayload) {
    openChatCommandsDrawer(activeChat.id)
}

function noCmd({ activeChat, mainCommand }: CommandPayload) {
    commandConfirmation({
        id: activeChat.id,
        error: `The ${mainCommand} command is not currently supported.`,
    })
}

async function defaultCmd({ args, activeChat }: CommandPayload) {
    if (args.length !== 1) {
        commandConfirmation({ id: activeChat.id, error: 'Unknown command.' })
        return
    }
    const slug = args[0].slice(1).toLowerCase()
    const user = await loadUserIfNeeded(slug)
    if (user && user.level !== UserAccess.MODEL) return
    return openRoom(slug)
}

function homepageCmd() {
    goToHome()
}

// MFC's lounges, and lounge-style member rooms, which only room state marks
const inLoungeRoom = ({ activeChat, chatType }: CommandPayload) =>
    isLounge(activeChat.id) ||
    (chatType === FCTYPE_CMESG && !!getRoomsState()[activeChat.id]?.isLounge)

function lastCmd(payload: CommandPayload) {
    const { activeChat, chatType } = payload
    if (chatType !== FCTYPE_CMESG) return
    const { id, slug: loungeSlug } = activeChat
    const isLounge = inLoungeRoom(payload)
    const { [id]: room } = getRoomsState()
    if (!room) {
        commandConfirmation({ id: activeChat.id, error: 'Command failed.' })
        return
    }
    const { previousLoungeSurrogateID } = room
    if (isLounge) {
        setLoungeSurrogate(previousLoungeSurrogateID, loungeSlug)
        return
    }

    const [{ visibleRoute: url }] = HISTORY.lastRoute
    if (!url) {
        commandConfirmation({ id: activeChat.id, error: 'No previous room.' })
        return
    }
    if (!url.startsWith('/room')) {
        routerPush(url)
        return
    }
    const [, , roomSlug] = url.split('/')
    if (roomSlug) {
        return openRoom(roomSlug)
    }
}

function nextCmd(payload: CommandPayload) {
    const { activeChat, chatType } = payload
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (inLoungeRoom(payload)) {
        setNextSurrogate(id)
        return
    }
    const nextId = getNextOrPreviousModel(id)
    return openRoom(nextId)
}

function prevCmd(payload: CommandPayload) {
    const { activeChat, chatType } = payload
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (inLoungeRoom(payload)) {
        setPreviousSurrogate(id)
        return
    }
    const prevId = getNextOrPreviousModel(id, { previous: true })
    return openRoom(prevId)
}

async function pmCmd(payload: CommandPayload) {
    const { args, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: locales.chat.pmWarningIfNotPremium })
        return
    }
    const slug = args.splice(1, 1).toString().toLowerCase()
    if (!slug) {
        commandConfirmation({
            id: activeChat.id,
            error: locales.chat.pmWithoutUsername,
        })
        return
    }
    // const { asPath } = Router.router
    if (AppRouter.route.visiblePage === 'message') {
        commandConfirmation({
            id: activeChat.id,
            error: locales.chat.pmFromPmWarning,
        })
        return
    }
    const user = await loadUserIfNeeded(slug)
    if (!user) {
        commandConfirmation({
            id: activeChat.id,
            error: locales.chat.pmWithoutValidUsername,
        })
        return
    }
    if (args.length < 2) return
    args.shift()
    const id = slugToID[slug]
    const message = args.join(' ')

    const onRouteChangeComplete = () => {
        // this setTimeout is needed to avoid a race condition with the message opening
        setTimeout(() => {
            sendPrivateMessage(id, message, true)
        }, 1000)
        AppRouter.events.offCompleted(onRouteChangeComplete)
    }
    AppRouter.events.onCompleted(onRouteChangeComplete)
    // wait for the message to open before sending the message to allow history to load
    await openMessage(slug)
}

async function whisperCmd(payload: CommandPayload) {
    const { args, chatType, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: locales.chat.pmWarningIfNotPremium })
        return
    }
    // const { asPath } = Router.router
    if (AppRouter.route.visiblePage === 'message') {
        commandConfirmation({
            id: activeChat.id,
            error: locales.chat.whisperFromPmWarning,
        })
        return
    }
    if (chatType !== FCTYPE_CMESG) return
    if (inLoungeRoom(payload)) {
        openError({ message: locales.chat.whisperFromLoungeWarning })
        return
    }
    if (args.length < 2) return
    args.shift()
    const message = args.join(' ')
    return sendPrivateMessage(activeChat.id, message, true)
}

async function meCmd({ args, activeChat, chatType }: CommandPayload) {
    if (args.length < 2) return
    args.shift()
    const { id } = activeChat
    const message = args.join(' ')
    if (chatType === FCTYPE_CMESG) {
        return sendRoomMessage(id, '{*emote*} ' + message)
    } else if (chatType === FCTYPE_PMESG) {
        return sendPrivateMessage(id, '{*emote*} ' + message)
    }
}

async function sarcasmCmd({ args, activeChat, chatType }: CommandPayload) {
    if (args.length < 2) return
    args.shift()
    const { id } = activeChat
    const message = args.join(' ')
    if (chatType === FCTYPE_CMESG) {
        return sendRoomMessage(id, '*sarcasm* ' + message)
    } else if (chatType === FCTYPE_PMESG) {
        return sendPrivateMessage(id, '*sarcasm* ' + message)
    }
}

async function noteCmd(payload: CommandPayload) {
    const { args, id, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: locales.chat.noteCommandWithoutPermission })
        return
    }
    const resolvedId = id ?? activeChat.id
    const inLounge = id ? false : inLoungeRoom(payload)
    const userId =
        id ??
        (!inLounge
            ? activeChat.id
            : getRoomsState()[resolvedId]?.loungeSurrogateID)
    const { token } = APPSTATE.session
    if (args.length === 0 || !token || !userId) {
        commandConfirmation({ id: activeChat.id, error: 'Note not saved.' })
        return
    }
    const date = MfcUserNotepad.generateEntryDate()
    const note = date + '\n' + args.slice(1).join(' ')
    return saveNote(token, userId, note)
        .then(() => {
            commandConfirmation({
                id: activeChat.id,
                message: 'Note saved successfully.',
            })
        })
        .catch(() => {
            commandConfirmation({
                id: activeChat.id,
                error: 'Note not saved.',
            })
        })
}

async function noteForCmd(payload: CommandPayload) {
    const { args, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: locales.chat.noteCommandWithoutPermission })
        return
    }
    // grab the model name from the second word
    const slug = args.splice(1, 1).toString().toLowerCase()
    if (!slug) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must include a username.',
        })
        return
    }
    const user = await loadUserIfNeeded(slug)
    if (!user) {
        commandConfirmation({ id: activeChat.id, error: 'User not found.' })
        return
    }
    const { id } = user
    return noteCmd({ ...payload, id })
}

async function tipCmd(
    payload: CommandPayload & { slug?: string; isTipFor?: boolean },
) {
    const { args, slug, activeChat, isPremium, isTipFor } = payload
    if (!isPremium) {
        openError({ message: locales.chat.tipCommandWithoutPermission })
        return
    }
    const resolvedSlug = slug ?? activeChat.slug ?? idToSlug[activeChat.id]
    const inLounge = slug ? false : inLoungeRoom(payload)
    const target =
        slug ??
        (!inLounge
            ? resolvedSlug
            : idToSlug[
                  getRoomsState()[slugToID[resolvedSlug]]?.loungeSurrogateID
              ])
    if (!target) {
        commandConfirmation({ id: activeChat.id, error: 'Tip not sent.' })
        return
    }
    const { amount, error, payloadOptions, repetitionCount, delay } =
        buildTipPayloadOptions(args)
    if (Number.isNaN(amount) || amount < 1) {
        const error = 'You must include a positive token amount.'
        commandConfirmation({ id: activeChat.id, error })
        return
    }
    if (error) {
        commandConfirmation({ id: activeChat.id, error })
        return
    }
    getLens()?.Tip_Submit({
        fromChat: true,
        ...(repetitionCount ? { repeatedTip: true } : {}),
        ...(isTipFor ? { tipFor: true } : {}),
    })
    if (repetitionCount) {
        const callback = (args: { message?: string; error?: string } = {}) =>
            commandConfirmation({ id: activeChat.id, tip: true, ...args })
        return sendTipRepeatedly(
            target,
            amount,
            payloadOptions,
            repetitionCount,
            delay,
            callback,
        )
    }
    return sendTip(target, amount, payloadOptions)
        .then(async () => {
            const user = await loadUserIfNeeded(target)
            const username = user?.name ?? target
            const targetStr = isTipFor ? `${username} ` : ''
            const message = `You have tipped ${targetStr}${toTokens(amount)}.`
            commandConfirmation({ id: activeChat.id, message, tip: true })
        })
        .catch(({ message }: Error) => {
            commandConfirmation({ id: activeChat.id, error: message })
        })
}

async function tipForCmd(payload: CommandPayload) {
    const { args, activeChat, isPremium } = payload
    if (!isPremium) {
        openError({ message: locales.chat.tipCommandWithoutPermission })
        return
    }
    // grab the model name from the second word
    const slug = args.splice(1, 1).toString().toLowerCase()
    if (!slug || !isNaN(Number(slug))) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must include a model username.',
        })
        return
    }
    return tipCmd({ ...payload, slug, isTipFor: true })
}

async function shareTipCmd({ args, activeChat, isPremium }: CommandPayload) {
    if (!isPremium) {
        openError({ message: locales.chat.tipCommandWithoutPermission })
        return
    }
    if (!activeChat) return
    const slug = activeChat.slug ?? idToSlug[activeChat.id]
    if (!slug) return
    // safely grab grab tip amount if there is one
    const getAmount = (): [number?, string?] => {
        try {
            return [Number(args[1]), null]
        } catch (_e) {
            return [null, 'You must supply a number for the token amount.']
        }
    }
    const [amount, error] = getAmount()
    if (!(amount > 0)) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must supply a positive token amount.',
        })
        return
    }
    if (error) {
        commandConfirmation({ id: activeChat.id, error })
        return
    }
    // grab the target from the second word
    const target = args[2]?.toString()?.toLowerCase()
    if (!target) {
        commandConfirmation({
            id: activeChat.id,
            error: 'You must include a share code or model username.',
        })
        return
    }
    // grab the optional message from the third word
    const message = args[3]?.toString()?.toLowerCase() ?? ''
    getLens()?.Tip_Submit({ shareTip: true })
    return sendShareTip(slug, amount, target, message).catch(
        ({ message }: Error) => {
            commandConfirmation({ id: activeChat.id, message, shareTip: true })
        },
    )
}

function quickBuyCmd({
    args,
    chatType,
    activeChat,
    isPremium,
}: CommandPayload) {
    if (!isPremium) {
        openError({ message: locales.chat.quickbuyCommandWithoutPermission })
        return
    }
    const { slug } = activeChat
    const tokenVolume = args[1]
    if (!tokenVolume) {
        return getTokenPurchaseInfo().then(({ config }) => {
            inChatQuickBuyDialog({
                ...config,
                location: {
                    slug,
                    type:
                        chatType === FCTYPE_CMESG
                            ? 'room'
                            : chatType === FCTYPE_PMESG
                              ? 'message'
                              : undefined,
                },
            })
        })
    }
    return tokensQualifyPurchase({
        chat_command_purchase: 1,
        volume: tokenVolume,
        volume_type: 'token',
    })
}

async function toStorageCmd({ args, activeChat, isPremium }: CommandPayload) {
    if (!isPremium) {
        openError({
            message: locales.chat.tokenStorageCommandWithoutPermission,
        })
        return
    }
    const tokenVolume = Number(args[1])
    if (!tokenVolume) return
    const { message, errmesg } = await shiftTokenStorage(tokenVolume)
    if (errmesg) {
        commandConfirmation({ id: activeChat.id, error: errmesg })
        return
    }
    commandConfirmation({ id: activeChat.id, message })
}

async function fromStorageCmd({ args, activeChat, isPremium }: CommandPayload) {
    if (!isPremium) {
        openError({
            message: locales.chat.tokenStorageCommandWithoutPermission,
        })
        return
    }
    const tokenVolume = Number(args[1])
    if (!tokenVolume) return
    const { message, errmesg } = await shiftTokenStorage(-tokenVolume)
    if (errmesg) {
        commandConfirmation({ id: activeChat.id, error: errmesg })
        return
    }
    commandConfirmation({ id: activeChat.id, message })
}

type CommandConfirmationProps = {
    id: number
    error?: string
    message?: string
    shareTip?: boolean
    tip?: boolean
}

export function commandConfirmation(payload: CommandConfirmationProps) {
    const { id, error, message, shareTip: shareTipConfirmation, tip } = payload
    const text = error
        ? error.slice(-1) === '.'
            ? `Error: ${error}`
            : `Error: ${error}.`
        : message
    const msg: Message = {
        text,
        timestamp: Date.now(),
        confirmation: !error,
        shareTipConfirmation,
        system: true,
        tip: tip && !error,
        error,
    }

    const quickReplyOpen = isQuickReplyOpen(id)
    const floatingRoom = getModalState().floating_room
    const roomView = floatingRoom.show && floatingRoom.roomView
    if (roomView === RoomView.Room) {
        setRoomMessagesState(msg, id)
    }
    if (roomView === RoomView.PM || quickReplyOpen) {
        setPMChatTextState(id, msg)
    }
}

/* Room Helper Commands */

function topicCmd({ args, activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (!isRoomHelper(id)) {
        commandConfirmation({
            id,
            error: 'You must be a room helper to use this command.',
        })
        return
    }

    const topic = args.slice(1).join(' ')
    if (topic.length > 160) {
        commandConfirmation({
            id,
            error: 'Topic cannot exceed 160 characters.',
        })
        return
    }

    roomHelperSendTopic(id, topic)
}

function banCmd({ args, activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (!isRoomHelper(id)) {
        commandConfirmation({
            id,
            error: 'You must be a room helper to use this command.',
        })
        return
    }

    const clearchat = args.includes('-c')
    const filtered = args.filter((a) => a !== '-c')

    if (
        !filtered[1] ||
        (typeof filtered[1] === 'string' && !filtered[1].match(/\w/))
    ) {
        commandConfirmation({
            id,
            error: 'Enter the username of the user you wish to ban.',
        })
        return
    }

    const username = filtered[1].replace(/\W/g, '')
    const isGuest = isGuestUsername(username)
    let sid: number | undefined

    if (isGuest) {
        sid = resolveGuestSession(username, id)
        if (!sid) {
            commandConfirmation({
                id,
                error: `${username} not found.`,
            })
            return
        }
    }

    roomHelperSendBan(id, {
        username: isGuest ? undefined : username,
        sid,
        clearchat,
    })
}

function unbanCmd({ args, activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (!isRoomHelper(id)) {
        commandConfirmation({
            id,
            error: 'You must be a room helper to use this command.',
        })
        return
    }

    if (!args[1] || (typeof args[1] === 'string' && !args[1].match(/\w/))) {
        commandConfirmation({
            id,
            error: 'Enter the username of the user you wish to unban.',
        })
        return
    }

    const username = args[1].replace(/\W/g, '')
    const isGuest = isGuestUsername(username)
    let sid: number | undefined

    if (isGuest) {
        sid = resolveGuestSession(username, id)
        if (!sid) {
            commandConfirmation({
                id,
                error: `${username} not found.`,
            })
            return
        }
    }

    roomHelperSendBan(id, {
        username: isGuest ? undefined : username,
        sid,
        unban: true,
    })
}

function kickCmd({ args, activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (!isRoomHelper(id)) {
        commandConfirmation({
            id,
            error: 'You must be a room helper to use this command.',
        })
        return
    }

    if (!args[1] || (typeof args[1] === 'string' && !args[1].match(/\w/))) {
        commandConfirmation({
            id,
            error: 'Enter the username(s) of the user you wish to kick.',
        })
        return
    }

    const users: Array<string | number> = []

    for (let n = 1; n < args.length; n++) {
        const username = args[n].replace(/\W/g, '')
        const isGuest = isGuestUsername(username)

        if (isGuest) {
            const sid = resolveGuestSession(username, id)
            if (!sid) {
                commandConfirmation({
                    id,
                    error: `${username} not found.`,
                })
                return
            }
            users.push(sid)
        } else {
            users.push(username)
        }
    }

    const chan = resolveChannel(id, 'public')
    roomHelperSendKick(id, chan, users)
}

function muteCmd({ args, activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (!isRoomHelper(id)) {
        commandConfirmation({
            id,
            error: 'You must be a room helper to use this command.',
        })
        return
    }

    const clearchat = args.includes('-c')
    const filtered = args.filter((a) => a !== '-c')

    if (filtered.length < 2 || filtered.length > 3) {
        commandConfirmation({
            id,
            error: 'Mute Error: Wrong number of arguments.',
        })
        return
    }

    // Case-sensitive comparison matches desktop behavior:
    // only lowercase "basic" and "guest" trigger level mute,
    // anything else (including "Basic", "Guest") falls through
    // to individual user mute.
    const target = filtered[1]

    // Individual user mute
    if (target !== 'basic' && target !== 'guest') {
        const username = target.replace(/\W/g, '')
        const isGuest = isGuestUsername(username)
        let sid: number | undefined

        if (isGuest) {
            sid = resolveGuestSession(username, id)
            if (!sid) {
                commandConfirmation({
                    id,
                    error: `${username} not found.`,
                })
                return
            }
        }

        roomHelperSendMuteUser(id, {
            username: isGuest ? undefined : username,
            sid,
            clearchat,
        })
        return
    }

    // Level mute (guest / basic)
    const optmask = target === 'guest' ? FCOPT_GUESTMUTE : FCOPT_BASICMUTE
    const action = filtered[2]

    if (action === 'show' || !action) {
        const model = getStateUser(id)
        const flags = model?.flags ?? 0
        const muted = !!(flags & optmask)
        commandConfirmation({
            id,
            message: `${target} mute is currently ${muted ? 'ON' : 'OFF'}.`,
        })
        return
    }

    if (action === 'on') {
        roomHelperSendMuteLevel(id, optmask, optmask)
    } else if (action === 'off') {
        roomHelperSendMuteLevel(id, optmask, 0)
    }
}

function unmuteCmd({ args, activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (!isRoomHelper(id)) {
        commandConfirmation({
            id,
            error: 'You must be a room helper to use this command.',
        })
        return
    }

    if (!args[1] || (typeof args[1] === 'string' && !args[1].match(/\w/))) {
        commandConfirmation({
            id,
            error: 'Enter the username of the user you wish to unmute.',
        })
        return
    }

    const username = args[1].replace(/\W/g, '')
    const isGuest = isGuestUsername(username)
    let sid: number | undefined

    if (isGuest) {
        sid = resolveGuestSession(username, id)
        if (!sid) {
            commandConfirmation({
                id,
                error: `${username} not found.`,
            })
            return
        }
    }

    roomHelperSendMuteUser(id, {
        username: isGuest ? undefined : username,
        sid,
        unmute: true,
    })
}

function countdownCmd({ args, activeChat, chatType }: CommandPayload) {
    if (chatType !== FCTYPE_CMESG) return
    const { id } = activeChat
    if (!isRoomHelper(id)) {
        commandConfirmation({
            id,
            error: 'You must be a room helper to use this command.',
        })
        return
    }

    // Validate inputs

    const errors: Array<string> = []

    if (!args[1]) {
        errors.push('Token account required. Type /? for help.')
    } else if (!args[1].match(/^[+-]?\d+$/) && args[1] !== 'none') {
        errors.push(`'${args[1]}' is not a valid countdown option.`)
    }

    const { [id]: room } = getRoomsState()
    const currentCountdown = room?.countdown

    if (
        args[1]?.match(/^[+-]\d+$/) &&
        (!currentCountdown || !currentCountdown.active)
    ) {
        errors.push('No active countdown to adjust.')
    }

    let parsedArg2: number | undefined
    if (args[2] && args[2].match(/\S/)) {
        if (!args[2].match(/^\d+$/)) {
            errors.push(`'${args[2]}' is not a valid second countdown option.`)
        } else {
            parsedArg2 = parseInt(args[2])
        }
    }

    if (errors.length > 0) {
        commandConfirmation({ id, error: errors.join(', ') })
        return
    }

    // Build payload (copy existing countdown data first)

    const hPayload: Record<string, any> = {}
    if (currentCountdown) {
        hPayload.total = currentCountdown.total
        hPayload.sofar = currentCountdown.sofar
        hPayload.countdown = currentCountdown.active
    }
    if (hPayload.sofar === undefined) hPayload.sofar = 0
    if (hPayload.countdown === undefined) hPayload.countdown = true

    hPayload.type = FCTYPE_ROOMDATA
    hPayload.model = id

    if (args[1].match(/^\d+$/)) {
        hPayload.total = parseInt(args[1])
        if (parsedArg2 !== undefined) {
            hPayload.sofar = hPayload.total - parsedArg2
        }
    } else if (args[1] === 'none') {
        hPayload.countdown = false
    } else {
        const subtractMatch = args[1].match(/^-(\d+)$/)
        const addMatch = args[1].match(/^\+(\d+)$/)
        if (subtractMatch) {
            hPayload.sofar = hPayload.sofar - parseInt(subtractMatch[1])
        } else if (addMatch) {
            hPayload.sofar = hPayload.sofar + parseInt(addMatch[1])
        }
    }

    // Validate result

    const errors2: Array<string> = []
    if (hPayload.sofar < 0) {
        errors2.push(
            'You cannot add more than the total number of tokens to the count.',
        )
    } else if (hPayload.sofar > hPayload.total) {
        errors2.push(
            'The amount earned so far cannot exceed the tokens remaining.',
        )
    }

    if (errors2.length > 0) {
        commandConfirmation({ id, error: errors2.join(', ') })
        return
    }

    roomHelperSendCountdown(id, hPayload)
}
